import { ChangeEvent, createContext, RefObject, useContext } from "react";
import { action, computed, observable } from "mobx";
import { fromEnv } from "../../../helpers";
import Compressor from "compressorjs";

type FilePickerStateOption = {
  maxSize?: number;
  previewUrl?: string;
  isError?: boolean;
  isLoading?: boolean;
  helperText?: string;
  temporaryFile?: File;
  metadata?: any;
  mediaType?: string;
};

export class FilePickerState {
  @observable
  _previewUrl!: string;

  @observable
  private _temporaryFile!: File | null;

  @observable
  errorMessage?: string;

  @observable
  maxSize = 15;

  @observable
  _isLoading = false;

  @observable
  helperText?: string;

  @observable
  isDragActive = false;

  @observable
  allowRetries = false;

  @observable
  metadata: any = {};

  @observable
  mediaType = "";

  @observable
  _isCompressing = false;

  imageSizeThreshold = Number(fromEnv("REACT_APP_IMAGE_TO_COMPRESS_MIN_SIZE"));

  dispose: any;

  acceptingFiles = ["image/jpg", "image/jpeg", "image/png", "application/pdf"];

  constructor(
    options?: FilePickerStateOption,
    public onChange?: (file: File) => void,
    public onInfoClick?: () => void,
    public translate?: (...args: any) => string,
    public inputRef?: RefObject<HTMLInputElement>,
    public onDelete?: () => void,
    public onClick?: () => void,
    public onError?: (errors: Error) => void
  ) {
    this.maxSize = options?.maxSize! || this.maxSize;
    this._previewUrl = options?.previewUrl!;
    this._isLoading = options?.isLoading!;
    this.helperText = options?.helperText!;
    this._temporaryFile = options?.temporaryFile!;
    this.errorMessage = options?.isError!
      ? this.translate!("error.message.network_error")
      : "";
    this.allowRetries = options?.isError!;
    this.metadata = options?.metadata;
    this.mediaType = options?.mediaType!;
  }

  @computed
  get isLoading(): boolean {
    return this._isLoading || this._isCompressing;
  }

  @computed
  get temporaryFile(): File {
    return this._temporaryFile!;
  }

  @computed
  get previewUrl(): string {
    return this.fileType === "application/pdf" && this._previewUrl
      ? `https://docs.google.com/viewer?url=${this._previewUrl!}&embedded=true`
      : this._previewUrl!;
  }

  @action
  onDrop = (acceptedFiles?: File[]) => {
    this.allowRetries = false;
    if (acceptedFiles?.length! > 1) {
      return (this.errorMessage = this.translate!(
        "error.message.too_many_files"
      ));
    }

    acceptedFiles?.forEach((file) => {
      this.setFile(file);

      if (!this.allowedFileType(file.type)) {
        this.onError?.(new Error(file.type));
        return (this.errorMessage = this
          .translate!("error.message.file_invalid_type", [
          `${this.maxSize}MB`,
        ]));
      }

      if (
        file.type !== "application/pdf" &&
        file.size > this.imageSizeThreshold
      ) {
        const that = this;
        try {
          this._isCompressing = true;
          new Compressor(file, {
            quality: Number(
              fromEnv("REACT_APP_IMAGE_COMPRESSION_QUALITY_RATE")
            ),
            convertSize: this.imageSizeThreshold,
            success(blob: Blob): void {
              that.onCompressSuccess(blob, file.name, file.type);
            },
            error(error: Error): void {
              that.onCompressError(error);
            },
          });
        } catch (e) {
          this.onCompressFailed(this.temporaryFile);
        }
      } else {
        this.onCompressFailed(this.temporaryFile);
      }
    });
  };

  allowedFileType = (type: string): boolean => {
    return this.acceptingFiles.includes(type);
  };

  allowedFileSize = (size: number): boolean => {
    return size <= this.maxSize * 1024 * 1024;
  };

  onFileSizeExceed = (error: string): void => {
    this.onError?.(new Error(error));
    this.errorMessage = this.translate!("error.message.file_too_large", [
      `${this.maxSize}MB`,
    ]);
  };

  onCompressSuccess = (blob: Blob, name: string, type: string) => {
    this._isCompressing = false;
    const compressed = new File([blob], name, {
      type,
    });
    if (!this.allowedFileSize(compressed.size)) {
      return this.onFileSizeExceed(compressed.type);
    }
    return this.onChange?.(compressed);
  };

  onCompressError = (error: Error) => {
    this.onError?.(error);
    return (this.errorMessage = this
      .translate!("error.message.file_invalid_type", [`${this.maxSize}MB`]));
  };

  onCompressFailed = (file: File) => {
    this._isCompressing = false;
    if (!this.allowedFileSize(file.size)) {
      return this.onFileSizeExceed(file.type);
    }
    this.onChange?.(this.temporaryFile);
  };

  @action
  setFile = (file: File) => {
    this._temporaryFile = file;
  };

  @action
  deleteFile = () => {
    this._temporaryFile = null;
    this._previewUrl = "";
    this.errorMessage = undefined;
    this.onDelete?.();
  };

  @action
  onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.onDrop(Array.from(event?.target?.files!));
  };

  @computed
  get caption(): string {
    const fileNameFromUrl = this._previewUrl?.split("/").pop();
    return this.temporaryFile?.name! || this.metadata?.tag! || fileNameFromUrl!;
  }

  @computed
  get fileType(): string {
    const fileTypeFromUrl = this._previewUrl?.toLowerCase().split(".").pop();

    return (
      this.temporaryFile?.type ||
      this.mediaType ||
      this.getMimeTypeFromFileExtension(fileTypeFromUrl!)
    );
  }

  get isPdf(): boolean {
    return this.fileType === "application/pdf";
  }

  getMimeTypeFromFileExtension = (fileType: string) => {
    switch (fileType) {
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      case "png":
        return "image/png";
      case "pdf":
        return "application/pdf";
      default:
        return fileType;
    }
  };

  retry = () => {
    this.onDrop([this.temporaryFile]);
  };
}

export const FilePickerContext = createContext(new FilePickerState());

export const useFilePickerState = () => useContext(FilePickerContext);
